import { useEffect, useState } from "react";
import { fmtInt, mapFill, type BreakdownRow } from "../../web-analytics";

type Location = { id: string; name: string; path: string };
type WorldData = { viewBox: string; locations: Location[] };

// The 256 country outlines are ~1.2 MB of static geometry. The app is one
// IIFE bundle, so importing them would put that weight on every page of NOLA.
// They live in web/public/world-map.json instead (scripts/build-world-map.mjs),
// fetched the first time a map mounts and kept here for every later one.
let world: Promise<WorldData> | null = null;

function loadWorld(): Promise<WorldData> {
  world ??= fetch("/world-map.json")
    .then((res) => {
      if (!res.ok) throw new Error(`world-map.json ${res.status}`);
      return res.json() as Promise<WorldData>;
    })
    .catch((error: unknown) => {
      // Forget a failed fetch, so the next mount tries again.
      world = null;
      throw error;
    });
  return world;
}

/**
 * Countries shaded by visitors: navy ink mixed into the chart track, on a
 * square-root scale. Hover (or long-press) a country for its name and count.
 * Ported from Sonar's WorldMap.
 */
export default function WorldMap({ rows }: { rows: BreakdownRow[] }) {
  const [data, setData] = useState<WorldData | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    loadWorld().then((w) => { if (live) setData(w); }, () => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  // The list beside the map carries the same numbers, so a failed fetch only
  // loses the picture; the box keeps its size either way.
  if (!data) return <div className="wa-map wa-map-pending" aria-hidden="true">{failed ? "Map unavailable" : null}</div>;

  const max = Math.max(0, ...rows.map((r) => r.visitors));
  const byCode = new Map(rows.map((r) => [r.value.toLowerCase(), r.visitors]));
  const shaded = data.locations.filter((l) => byCode.has(l.id)).length;

  return (
    <svg
      className="wa-map"
      viewBox={data.viewBox}
      role="img"
      aria-label={`World map showing visitors from ${shaded} ${shaded === 1 ? "country" : "countries"}`}
    >
      {data.locations.map((l) => {
        const visitors = byCode.get(l.id);
        // One string child: React renders <title> from a single text node.
        const label = `${l.name}: ${visitors ? `${fmtInt(visitors)} visitor${visitors === 1 ? "" : "s"}` : "No visits"}`;
        return (
          <path key={l.id} d={l.path} fill={mapFill(visitors, max)}>
            <title>{label}</title>
          </path>
        );
      })}
    </svg>
  );
}
